import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Loader2, Stamp } from 'lucide-react';
import { toast } from 'sonner';
import { buildSealSvg, type SealStyle } from '@/components/workflows/sealModel';

interface SealSettingsCardProps {
  unaId: string | null;
}

export function SealSettingsCard({ unaId }: SealSettingsCardProps) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState('');
  const [enabled, setEnabled] = useState(false);
  const [year, setYear] = useState('');

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!unaId) {
        setLoading(false);
        return;
      }
      const { data } = await supabase
        .from('unas')
        .select('name, seal_enabled, seal_style')
        .eq('id', unaId)
        .maybeSingle();
      if (cancelled || !data) {
        setLoading(false);
        return;
      }
      setName((data as any).name || '');
      setEnabled(Boolean((data as any).seal_enabled));
      const style = ((data as any).seal_style as SealStyle | null) ?? null;
      setYear(style?.established_year ? String(style.established_year) : '');
      setLoading(false);
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [unaId]);

  async function save() {
    if (!unaId) return;
    setSaving(true);
    const style: SealStyle | null = year.trim() ? { established_year: year.trim() } : null;
    const { error } = await supabase
      .from('unas')
      .update({ seal_enabled: enabled, seal_style: style as any })
      .eq('id', unaId);
    setSaving(false);
    if (error) {
      toast.error('We could not save that. Please try again.');
      return;
    }
    toast.success('Seal settings saved');
  }

  if (loading) return null;

  const previewSvg = buildSealSvg(name || 'Your Association', year.trim() ? { established_year: year.trim() } : null);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Stamp className="h-5 w-5" />
          Seal
        </CardTitle>
        <CardDescription>
          A seal is optional tradition. Some Associations keep one; many do not. Yours applies only where you choose.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between gap-4">
          <Label htmlFor="seal-enabled" className="font-normal">
            Keep a seal for this Association
          </Label>
          <Switch id="seal-enabled" checked={enabled} onCheckedChange={setEnabled} />
        </div>

        {enabled && (
          <>
            <div className="space-y-2">
              <Label htmlFor="seal-year">Year established (optional)</Label>
              <Input
                id="seal-year"
                value={year}
                onChange={(e) => setYear(e.target.value)}
                placeholder="2026"
                inputMode="numeric"
                className="max-w-[160px]"
              />
            </div>
            <div className="rounded-lg border bg-muted/40 p-4 flex justify-center">
              <div dangerouslySetInnerHTML={{ __html: previewSvg }} />
            </div>
          </>
        )}

        <div className="flex justify-end">
          <Button onClick={save} disabled={saving} variant="outline">
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save seal settings
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default SealSettingsCard;
